import { BaseAPIService } from '../api/base';
import { supabase } from '../api/client';

/**
 * MPT (Multistate Performance Test) Service
 * Handles all MPT exam-related API calls
 */
class MPTService extends BaseAPIService {
  constructor() {
    super('mpt_tests');
  }

  /**
   * Get MPT tests
   */
  async getTests(options = {}) {
    try {
      let query = supabase
        .from('mpt_tests')
        .select(options.select || '*');

      query = query.order('created_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MPT tests:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get test files (library and task memo)
   */
  async getTestFiles(testId) {
    try {
      const { data, error } = await supabase
        .from('mpt_files')
        .select('*')
        .eq('test_id', testId)
        .order('file_order', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MPT files:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save MPT submission
   */
  async saveSubmission(userId, testId, submission) {
    try {
      const { data, error } = await supabase
        .from('mpt_submissions')
        .insert([
          {
            user_id: userId,
            test_id: testId,
            submission_text: submission,
            submitted_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving MPT submission:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save draft
   */
  async saveDraft(userId, testId, draftContent) {
    try {
      const { data, error } = await supabase
        .from('mpt_drafts')
        .upsert([
          {
            user_id: userId,
            test_id: testId,
            draft_content: draftContent,
            updated_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving MPT draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get draft
   */
  async getDraft(userId, testId) {
    try {
      const { data, error } = await supabase
        .from('mpt_drafts')
        .select('*')
        .eq('user_id', userId)
        .eq('test_id', testId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MPT draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Request AI evaluation
   */
  async requestEvaluation(userId, submissionId) {
    try {
      const { data, error } = await supabase
        .from('mpt_evaluations')
        .insert([
          {
            user_id: userId,
            submission_id: submissionId,
            status: 'pending',
            requested_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      
      // Trigger AI evaluation
      // This would typically call an edge function or external API
      
      return { success: true, data };
    } catch (error) {
      console.error('Error requesting MPT evaluation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get evaluation results
   */
  async getEvaluation(evaluationId) {
    try {
      const { data, error } = await supabase
        .from('mpt_evaluations')
        .select('*')
        .eq('id', evaluationId)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MPT evaluation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user progress
   */
  async getUserProgress(userId) {
    try {
      const { data, error } = await supabase
        .from('mpt_submissions')
        .select('*, mpt_evaluations(*)')
        .eq('user_id', userId);

      if (error) throw error;

      const stats = {
        totalSubmissions: data.length,
        evaluatedSubmissions: data.filter(s => s.mpt_evaluations?.length > 0).length,
        averageScore: 0,
        completionRate: 0,
      };

      // Calculate average score
      const scores = data
        .flatMap(s => s.mpt_evaluations || [])
        .filter(e => e.score !== null)
        .map(e => e.score);

      if (scores.length > 0) {
        stats.averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
      }

      // Calculate completion rate
      const { data: testsData } = await supabase
        .from('mpt_tests')
        .select('id');
      
      if (testsData) {
        stats.completionRate = (data.length / testsData.length) * 100;
      }

      return { success: true, data: { submissions: data, stats } };
    } catch (error) {
      console.error('Error fetching MPT progress:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save notes for a test
   */
  async saveNotes(userId, testId, notes) {
    try {
      const { data, error } = await supabase
        .from('mpt_notes')
        .upsert([
          {
            user_id: userId,
            test_id: testId,
            notes,
            updated_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving MPT notes:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get notes for a test
   */
  async getNotes(userId, testId) {
    try {
      const { data, error } = await supabase
        .from('mpt_notes')
        .select('*')
        .eq('user_id', userId)
        .eq('test_id', testId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MPT notes:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const mptService = new MPTService();
export default mptService;